import type * as Types from '@aeppic/types'
import type Aeppic from '../aeppic.js'

import { ParsedField } from '@aeppic/forms-parser'
import { FormBasedLookup, LookupEntry, LookupFunction, LookupTrace, ancestorTraversal, checkInReferencedFolders, form, getFullFormAncestry, lookForFoldersOfType } from './form-oriented-lookup.js'
import { controlNameForPlaceholder, resolveControlSelector, normalizeControlLookupVariant, type ControlLookupVariant } from '../forms/preload-controls.js'
import type { ParsedPlaceholder } from '@aeppic/forms-parser'
import type { AeppicInterface } from '../aeppic.js'

const CONTROL_FORM = 'control-form'
const CONTROL_FOLDER_FORM = 'control-folder'

const DEFAULT_FIND_OPTIONS = {
  limit: 100,
  sort: 'created.at',
  fields: []
}

export enum ControlMatchStrategy {
  FULL,
  SYSTEM,
  DEFAULT_ONLY
}

type FieldLookupInfo = {
  field: ParsedField
}

const SYSTEM_CONTROLS_FOLDER = 'controls-folder'
const SYSTEM_CONTROLS_DEFAULT_FOLDER = '91f205bc-9989-4fbe-8966-bd4ba9777c26'

// Strategies
//
// Ids or Functions that will be called in order to find the control
//
// Each Id is a folder id to look in.
//
// Each function returns an AsyncIterableIterator of the folder ids
// to look in.
const CONTROL_FOLDER_LOOKUP_ORDER_DEFAULT: LookupEntry[] = [
  SYSTEM_CONTROLS_DEFAULT_FOLDER,
]

const CONTROL_FOLDER_LOOKUP_ORDER_SYSTEM: LookupEntry[] = [
  SYSTEM_CONTROLS_FOLDER,
  ...CONTROL_FOLDER_LOOKUP_ORDER_DEFAULT
]

const FORMS_WITH_REFERENCED_CONTROLS: Types.DocumentId[] = [
  'form-folder',
  'application-form',
  'root-folder-form',
]

const CONTROL_FOLDER_LOOKUP_ORDER_FULL: LookupEntry[] = [
  form,
  ancestorTraversal(
    getFullFormAncestry,
    [
      lookForControlFolders(),
      checkInReferencedFolders(
        'controls',
        FORMS_WITH_REFERENCED_CONTROLS
      )
    ],
  ),
  ...CONTROL_FOLDER_LOOKUP_ORDER_SYSTEM
]

export class ControlLookup {
  private _lookup: FormBasedLookup<FieldLookupInfo>
  private _strategy: LookupEntry[]
  
  constructor(private aeppic: AeppicInterface, strategy: ControlMatchStrategy, options: { warnOnMiss?: boolean } = {}) {
    this._strategy =
        strategy == ControlMatchStrategy.FULL ? CONTROL_FOLDER_LOOKUP_ORDER_FULL
      : strategy == ControlMatchStrategy.SYSTEM ? CONTROL_FOLDER_LOOKUP_ORDER_SYSTEM
      : strategy == ControlMatchStrategy.DEFAULT_ONLY ? CONTROL_FOLDER_LOOKUP_ORDER_DEFAULT
      : CONTROL_FOLDER_LOOKUP_ORDER_FULL

    this._lookup = new FormBasedLookup(
      aeppic,
      'control', this._strategy, findBestMatchingControlInFolder, options
    )
  }
  
  async findControlDocument(formId: Types.DocumentId, field: ParsedField|null, controlSelector: string, lookupTrace?: LookupTrace, variant: ControlLookupVariant = {}): Promise<Types.Document|null> {
    const selector = resolveControlSelector(controlSelector, field, variant)
    if (!selector) return null
    const { namespace, name } = selector

    if (lookupTrace) {
      lookupTrace.namespace = namespace
      lookupTrace.name = name
      lookupTrace.field = field
    }
    
    const extra: FieldLookupInfo = { field }

    return this._lookup.find(formId, namespace, name, extra, lookupTrace)
  }

  /** Resolve lazily through the individual matcher; reuse identical reads within this map. */
  async findControlsForPlaceholders(form: { id: Types.DocumentId, getPlaceholders: () => ParsedPlaceholder[] }, startFind: (query) => Promise<Types.Document[]>, variant: ControlLookupVariant = {}): Promise<Map<number, Types.Document | null>> {
    normalizeControlLookupVariant(variant)
    const pending = new Map<string, Promise<any>>()
    const memo = (kind, args, load) => {
      const key = JSON.stringify([kind, ...args])
      if (!pending.has(key)) pending.set(key, Promise.resolve().then(load))
      return pending.get(key)
    }
    const source = this.aeppic
    const scoped = Object.create(source)
    scoped.find = (query, options) => memo('find', [query, options], () => startFind(query))
    scoped.findOne = (query, options) => memo('findOne', [query, options], () => source.findOne(query, options))
    scoped.get = identifier => memo('get', [identifier], () => source.get(identifier))
    const lookup = new ControlLookup(scoped, this._strategyEnum())
    const result = new Map<number, Types.Document | null>()
    for (const placeholder of form.getPlaceholders()) {
      if (placeholder.offset == null || (!placeholder.field && !placeholder.control)) continue
      result.set(placeholder.offset, await lookup.findControlDocument(form.id, placeholder.field ?? null,
        controlNameForPlaceholder(placeholder), undefined, variant))
    }
    return result
  }

  private _strategyEnum(): ControlMatchStrategy {
    if (this._strategy === CONTROL_FOLDER_LOOKUP_ORDER_SYSTEM) return ControlMatchStrategy.SYSTEM
    if (this._strategy === CONTROL_FOLDER_LOOKUP_ORDER_DEFAULT) return ControlMatchStrategy.DEFAULT_ONLY
    return ControlMatchStrategy.FULL
  }

}

export function buildControlMatchQuery(aeppic: AeppicInterface, parentId: Types.DocumentId, field: ParsedField | null, namespace: string, name: string) {
  if (name === '*') return null
  let query = aeppic.Query.children(parentId)
    .form(CONTROL_FORM)

  if (field) {
    query = query.where('data.cardinality').isOneOf(field.cardinality ? ['BOTH', 'MULTIPLE'] : ['BOTH', 'SINGLE'])

    const types = []
    if (field.subType == 'select') {
      types.push('select_any')
      types.push(`select_${field.type}`)
    } else {
      types.push(field.subType)
    }
    if (field.type == 'string') {
      types.push('string')
    }

    query = query.and(q => {
      q.where('data.limitTypes').is(false)
      .or
      .where('data.types').isOneOf(types)
    })
  } else {
    query = query.where('data.standalone').isOneOf(['SUPPORTED', 'REQUIRED'])
  }

  return query
    .where('data.namespace').is(namespace)
    .where('data.name').isStrict(name).caseInsensitive()
}

/** Every folder the strategy would walk, nearest first, without stopping at a match. */
export async function listControlLocations(aeppic: AeppicInterface, formId: Types.DocumentId, strategy: ControlMatchStrategy): Promise<Types.DocumentId[]> {
  const entries =
      strategy == ControlMatchStrategy.FULL ? CONTROL_FOLDER_LOOKUP_ORDER_FULL
    : strategy == ControlMatchStrategy.SYSTEM ? CONTROL_FOLDER_LOOKUP_ORDER_SYSTEM
    : strategy == ControlMatchStrategy.DEFAULT_ONLY ? CONTROL_FOLDER_LOOKUP_ORDER_DEFAULT
    : CONTROL_FOLDER_LOOKUP_ORDER_FULL
  const locations: Types.DocumentId[] = []
  const seen = new Set<Types.DocumentId>()
  for (const lookup of entries) {
    if (typeof lookup == 'string') {
      if (seen.has(lookup)) continue
      seen.add(lookup)
      locations.push(lookup)
      continue
    }
    for await (const parentId of lookup(aeppic, formId, null, undefined)) {
      if (!parentId || seen.has(parentId)) continue
      seen.add(parentId)
      locations.push(parentId)
    }
  }
  return locations
}

async function findBestMatchingControlInFolder(aeppic: AeppicInterface, parentId: Types.DocumentId, _formId: Types.DocumentId, namespace: string, name: string, extra: FieldLookupInfo, trace?: LookupTrace): Promise<Types.Document> {
  const query = buildControlMatchQuery(aeppic, parentId, extra.field, namespace, name)
  if (!query) {
    trace?.addStep({ operation: 'find', documentId: parentId, additionalInfo: { query: null, options: DEFAULT_FIND_OPTIONS } })
    return null
  }

  const controls = await aeppic.find(query, DEFAULT_FIND_OPTIONS) 
  const isStandalone = !extra.field

  for (const control of controls) {
    if (!isStandalone) {
    }
    // if (field && control.data.cardinalityLimitation) {
    //   const { min, max } = control.data.cardinalityLimitation

    //   if (field.cardinality.min < min || field.cardinality.max > max) {
    //     continue
    //   }
    // }

    trace?.addStep({ operation: 'find:match', documentId: parentId, additionalInfo: { query, controls: controls.length, control } })
    return control
  }

  trace?.addStep({ operation: 'find', documentId: parentId, additionalInfo: { query: query, options: DEFAULT_FIND_OPTIONS, controls } })
  return null
}

// Check in each child of type 'control-folder' under the document 
function lookForControlFolders(): LookupFunction {
  return lookForFoldersOfType(CONTROL_FOLDER_FORM, DEFAULT_FIND_OPTIONS)
}
